/**
 * ABN/ACN Lookup Service
 * Integrates with free ABN and ACN lookup APIs
 * Uses configurable mock data from abnAcnConfig.ts
 */

import {
  CONFIGURED_ABN_DATA,
  LOOKUP_SIMULATION_DELAY,
  BACKEND_API_CONFIG,
  abnToAcn,
  isValidABNFormat,
  isValidACNFormat,
  findConfiguredABN,
  findConfiguredACN,
  ConfiguredABNData,
} from '../config/abnAcnConfig';

export interface ABNLookupResult {
  abn: string;
  name: string;
  status: string;
  acn?: string;
  businessType?: string;
  address?: string;
  isActive: boolean;
  austracType?: string;
}

export interface ACNLookupResult {
  acn: string;
  name: string;
  status: string;
  abn?: string;
  businessType?: string;
  address?: string;
  isActive: boolean;
  austracType?: string;
}

/**
 * Lookup ABN using live ABR API
 * 
 * @param abn - The ABN to lookup (11 digits)
 * @returns Business details or null if not found
 */
export async function lookupABN(abn: string): Promise<ABNLookupResult | null> {
  try {
    const cleanAbn = abn.replace(/\D/g, '');

    if (!isValidABNFormat(cleanAbn)) {
      return null;
    }

    // First check configured mock data (for demo/testing)
    const configuredData = findConfiguredABN(cleanAbn);
    if (configuredData) {
      // Simulate network delay for better UX
      await new Promise(resolve => setTimeout(resolve, LOOKUP_SIMULATION_DELAY));
      
      return {
        abn: configuredData.abn,
        name: configuredData.name,
        status: configuredData.status || 'Active',
        acn: abnToAcn(configuredData.abn),
        businessType: configuredData.businessType,
        address: configuredData.address,
        isActive: (configuredData.status || 'Active') === 'Active',
      };
    }

    // Call KYB API
    const response = await fetch(BACKEND_API_CONFIG.KYB_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ legalEntityCodeType: 'ABN', legalEntityCodeData: cleanAbn }),
      signal: AbortSignal.timeout(BACKEND_API_CONFIG.TIMEOUT_MS),
    });

    const data = await response.json();

    if (!response.ok || data.action === 'ERROR') {
      return null;
    }

    const statePostcode = [data.stateCode, data.postcode].filter(Boolean).join(' ');

    return {
      abn: data.resultLegalEntityCodeData || cleanAbn,
      name: data.legalEntityName || '',
      status: data.legalEntityStatus || 'Active',
      acn: abnToAcn(cleanAbn),
      businessType: data.austracType || data.legalEntityType,
      address: statePostcode,
      isActive: (data.legalEntityStatus || '').toLowerCase().includes('active'),
      austracType: data.austracType,
    };
  } catch (error) {
    return null;
  }
}

/**
 * Lookup ACN using configured data and backend API
 * 
 * @param acn - The ACN to lookup (9 digits)
 * @returns Business details or null if not found
 */
export async function lookupACN(acn: string): Promise<ACNLookupResult | null> {
  try {
    const cleanAcn = acn.replace(/\D/g, '');

    if (!isValidACNFormat(cleanAcn)) {
      return null;
    }

    // First check configured mock data (for demo/testing)
    const configuredData = findConfiguredACN(cleanAcn);
    if (configuredData) {
      // Simulate network delay for better UX
      await new Promise(resolve => setTimeout(resolve, LOOKUP_SIMULATION_DELAY));
      
      return {
        acn: cleanAcn,
        name: configuredData.name,
        status: configuredData.status || 'Active',
        abn: configuredData.abn,
        businessType: configuredData.businessType,
        address: configuredData.address,
        isActive: (configuredData.status || 'Active') === 'Active',
      };
    }

    // Call KYB API
    const response = await fetch(BACKEND_API_CONFIG.KYB_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ legalEntityCodeType: 'ACN', legalEntityCodeData: cleanAcn }),
      signal: AbortSignal.timeout(BACKEND_API_CONFIG.TIMEOUT_MS),
    });

    const data = await response.json();

    if (!response.ok || data.action === 'ERROR') {
      return null;
    }

    const statePostcode = [data.stateCode, data.postcode].filter(Boolean).join(' ');

    return {
      acn: data.resultLegalEntityCodeData || cleanAcn,
      name: data.legalEntityName || '',
      status: data.legalEntityStatus || 'Active',
      abn: data.resultLegalEntityCodeType === 'ABN' ? data.resultLegalEntityCodeData : '',
      businessType: data.austracType || data.legalEntityType,
      address: statePostcode,
      isActive: (data.legalEntityStatus || '').toLowerCase().includes('active'),
      austracType: data.austracType,
    };
  } catch (error) {
    return null;
  }
}


/**
 * Parse ABN to extract ACN (last 9 digits of ABN)
 * @param abn - The ABN string
 * @returns ACN extracted from ABN
 */
export function abnToAcnExport(abn: string): string {
  return abnToAcn(abn);
}

/**
 * Check if lookup is available (for offline mode)
 */
export function isLookupAvailable(): boolean {
  // In development, we'll use configured mock data
  // In production, check if real API is accessible
  return typeof window !== 'undefined';
}

/**
 * Get all configured test ABNs (for UI help/hints)
 */
export function getConfiguredTestABNs(): Array<{ abn: string; name: string }> {
  return CONFIGURED_ABN_DATA.map((entry: ConfiguredABNData) => ({
    abn: entry.abn,
    name: entry.name,
  }));
}
